'use client'

import { Drawer } from 'vaul'

import { SplitsLink } from '~/components/Link'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/component-library/Dropdown/primitives'
import { DropdownProps } from '~/components/component-library/Dropdown/types'
import { useIsMobile } from '~/utils/devices'
import { cn } from '~/utils/tailwind-utils'

/**
 * Declarative dropdown menu. Takes a trigger element as its child and renders
 * an `items` list — each entry is either a `DropdownItem` or the literal
 * `'divider'`. On mobile it opens as a bottom drawer instead of a popover. For
 * composition beyond this, use the re-exported primitives directly.
 */
export const Dropdown = (props: DropdownProps) => {
  const isMobile = useIsMobile()
  const align = props.align ?? 'end'
  if (align === 'end' && (props.side === 'left' || props.side === 'right')) {
    throw new Error(
      `Dropdown: align="end" cannot be paired with side="${props.side}". Use align="start" to anchor the menu to the top of the trigger.`,
    )
  }
  return isMobile ? (
    <MobileDropdown {...props} />
  ) : (
    <DesktopDropdown {...props} />
  )
}

const DesktopDropdown = ({
  children,
  items,
  align = 'end',
  side,
  open,
  onOpenChange,
}: DropdownProps) => (
  <DropdownMenu open={open} onOpenChange={onOpenChange}>
    <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
    <DropdownMenuContent align={align} side={side}>
      {items.map((item, index) => {
        if (item === 'divider') return <DropdownMenuSeparator key={index} />

        const body = (
          <>
            {item.icon}
            {item.content}
          </>
        )

        return (
          <DropdownMenuItem
            key={index}
            onClick={item.onClick}
            disabled={item.disabled}
            tooltip={item.tooltip}
            asChild={!!item.href}
          >
            {item.href ? (
              <SplitsLink
                path={item.href.path}
                target={item.href.newTab ? 'tab' : undefined}
              >
                {body}
              </SplitsLink>
            ) : (
              body
            )}
          </DropdownMenuItem>
        )
      })}
    </DropdownMenuContent>
  </DropdownMenu>
)

const drawerItemClass =
  'flex w-full select-none items-center gap-2.5 px-5 py-3 text-base outline-none transition-colors active:bg-surface-strong [&>svg]:size-4'

const MobileDropdown = ({
  children,
  items,
  open,
  onOpenChange,
}: DropdownProps) => (
  <Drawer.Root open={open} onOpenChange={onOpenChange}>
    <Drawer.Trigger asChild>{children}</Drawer.Trigger>
    <Drawer.Portal>
      <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
      <Drawer.Content
        aria-describedby={undefined}
        className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl border-t border-muted bg-surface-raised pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1 text-primary"
      >
        <Drawer.Handle className="my-2 !w-10 !bg-gray-300 dark:!bg-gray-700" />
        <Drawer.Title className="sr-only">Actions</Drawer.Title>
        <div className="flex flex-col py-1">
          {items.map((item, index) => {
            if (item === 'divider')
              return <div key={index} className="my-1 h-px bg-divider" />

            const body = (
              <>
                {item.icon}
                {item.content}
              </>
            )

            if (item.disabled)
              return (
                <div
                  key={index}
                  className={cn(
                    drawerItemClass,
                    'pointer-events-none opacity-50',
                  )}
                >
                  {body}
                </div>
              )

            return (
              <Drawer.Close key={index} asChild>
                {item.href ? (
                  <SplitsLink
                    path={item.href.path}
                    target={item.href.newTab ? 'tab' : undefined}
                    onClick={item.onClick}
                    className={drawerItemClass}
                  >
                    {body}
                  </SplitsLink>
                ) : (
                  <button
                    type="button"
                    className={drawerItemClass}
                    onClick={item.onClick}
                  >
                    {body}
                  </button>
                )}
              </Drawer.Close>
            )
          })}
        </div>
      </Drawer.Content>
    </Drawer.Portal>
  </Drawer.Root>
)

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
}
export type {
  DropdownProps,
  DropdownItem,
  DropdownEntry,
  DropdownItemHref,
} from '~/components/component-library/Dropdown/types'
